import axios from "axios";
import predefinedCreditorTypes from "@/common/data/creditor-types.data";

/**
 * Classes de credores por caso.
 *
 * As classes deixaram de ser uma lista fixa e passaram a ser configuradas em
 * cada caso (aba "Classes" no detalhe do caso). Este util centraliza a busca
 * para que as telas nao repitam a chamada nem a logica de cache.
 *
 * Dois usos distintos, com regras opostas:
 *
 *   - selects  -> apenas as classes ativas do caso (o que pode ser escolhido)
 *   - rotulos  -> todas as classes, inclusive ocultas, senao um credor
 *                 classificado numa classe oculta apareceria sem nome
 */

const cache = new Map();

function cacheKey(clientId, includeInactive) {
  return `${clientId}:${includeInactive ? "all" : "active"}`;
}

function normalize(item) {
  return {
    // id espelha o valor gravado em user_creditors.type, para que os v-model
    // existentes continuem funcionando sem conversao nas telas.
    id: item.legacy_class_id,
    name: item.name,
    is_active: item.is_active,
    is_predefined: item.is_predefined,
    base_class_id: item.base_class_id,
    participates_voting: item.participates_voting,
    requires_amount_majority: item.requires_amount_majority,
  };
}

/**
 * Classes de um caso. Sem clientId devolve as pre-definidas, que e o
 * comportamento adequado quando ainda nao ha caso escolhido.
 */
async function listByClient(clientId, options = {}) {
  const includeInactive = options.includeInactive === true;

  if (!clientId) return [...predefinedCreditorTypes];

  const key = cacheKey(clientId, includeInactive);

  if (cache.has(key)) return cache.get(key);

  try {
    const result = await axios.get(`/api/admin/clients/${clientId}/creditor-classes`, {
      params: includeInactive ? { include_inactive: true } : {},
    });

    const data = ((result.data.content && result.data.content.data) || []).map(normalize);

    cache.set(key, data);

    return data;
  } catch (error) {
    // Sem resposta da API a tela continua utilizavel com as classes padrao.
    return [...predefinedCreditorTypes];
  }
}

/**
 * Classes usadas apenas como rotulo (coluna de tabela, exportacao), em telas
 * que listam varios casos ao mesmo tempo.
 *
 * Inclui as classes ocultas de proposito: um credor ja classificado numa
 * classe oculta precisa continuar exibindo o nome dela, e nao "N/A".
 *
 * Quando ha um caso definido busca so as classes dele; sem caso, devolve as
 * pre-definidas acrescidas das personalizadas do caso corrente, evitando uma
 * requisicao por caso da listagem.
 */
async function listLabels(clientId = null) {
  const classes = await listByClient(clientId, { includeInactive: true });
  const merged = [...predefinedCreditorTypes];

  classes.forEach((item) => {
    if (merged.some((existing) => Number(existing.id) === Number(item.id))) return;
    merged.push(item);
  });

  return merged;
}

/**
 * Rotulos para listagens que cruzam varios casos ao mesmo tempo, onde o caso e
 * uma coluna e nao um filtro. Junta as classes de todos os casos informados,
 * sem repetir ids.
 *
 * As requisicoes usam o cache por caso, entao abrir a mesma listagem de novo
 * nao repete as chamadas.
 */
async function listLabelsForClients(clientIds = []) {
  const unique = [...new Set((clientIds || []).map(Number).filter(Boolean))];

  if (!unique.length) return [...predefinedCreditorTypes];

  const results = await Promise.all(
    unique.map((clientId) => listByClient(clientId, { includeInactive: true }))
  );

  const merged = [...predefinedCreditorTypes];

  results.forEach((classes) => {
    classes.forEach((item) => {
      if (merged.some((existing) => Number(existing.id) === Number(item.id))) return;
      merged.push(item);
    });
  });

  return merged;
}

/**
 * Limpa o cache de um caso. Chamado ao salvar/ocultar uma classe, para que os
 * selects reflitam a mudanca sem recarregar a pagina.
 */
function invalidate(clientId = null) {
  if (!clientId) {
    cache.clear();
    return;
  }

  cache.delete(cacheKey(clientId, true));
  cache.delete(cacheKey(clientId, false));
}

export default {
  listByClient,
  listLabels,
  listLabelsForClients,
  invalidate,
  predefined: predefinedCreditorTypes,
};
